import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { GraduationCap, Trash2, CalendarDays, Activity, Plane, AlertTriangle } from 'lucide-react';
import { api, extractError } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { fmtDate } from '../../lib/date';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { ABSENCE_TYPES, TYPE_BADGE, fmtDays } from './constants';

const WEEKDAYS = [
  { value: 0, label: 'Mo', full: 'Montag' },
  { value: 1, label: 'Di', full: 'Dienstag' },
  { value: 2, label: 'Mi', full: 'Mittwoch' },
  { value: 3, label: 'Do', full: 'Donnerstag' },
  { value: 4, label: 'Fr', full: 'Freitag' },
  { value: 5, label: 'Sa', full: 'Samstag' },
];

/**
 * Anlegen ODER Bearbeiten einer Abwesenheit.
 * - create: initial = { employee_id?, start_date? }
 * - edit:   editRange = { item_ids, employee_id, start_date, end_date, type, half_day, note, source }
 */
/**
 * Kurzer Zusatz für die Erfolgsmeldung: Was wurde aus der Planung genommen?
 *
 * Wird nichts entfernt, bleibt die Meldung wie vorher – ein Hinweis auf
 * "0 Einsätze" wäre nur Lärm.
 */
function planungsHinweis(antwort) {
  const p = antwort?.data?.planung;
  if (!p) return '';
  const teile = [];
  if (p.entfernt) teile.push(`${p.entfernt} Einsatz${p.entfernt === 1 ? '' : 'e'} aus der Planung genommen`);
  if (p.fahrer_frei) teile.push(`${p.fahrer_frei} Geräte-Einsatz ohne Fahrer`);
  if (p.vergangene_tage) teile.push(`${p.vergangene_tage} vergangene Tage unverändert`);
  return teile.length ? ` ${teile.join(', ')}.` : '';
}

export function AbsenceFormDialog({ open, onOpenChange, employees, onSaved, initial, editRange }) {
  const isEdit = !!editRange;
  const [form, setForm] = useState({ employee_id: '', start_date: '', end_date: '', type: 'Urlaub', half_day: false, note: '' });
  const [loading, setLoading] = useState(false);
  // Suchfeld in der Mitarbeiterauswahl. Bei dreißig Namen scrollt man sonst
  // und übersieht den Gesuchten.
  const [suche, setSuche] = useState('');
  const { confirm } = useConfirm();

  useEffect(() => {
    if (!open) return;
    if (editRange) {
      setForm({
        employee_id: editRange.employee_id || '',
        start_date: editRange.start_date || '',
        end_date: editRange.end_date && editRange.end_date !== editRange.start_date ? editRange.end_date : '',
        type: editRange.type || 'Urlaub',
        half_day: !!editRange.half_day,
        note: editRange.note || '',
      });
    } else {
      setForm({
        employee_id: initial?.employee_id || '',
        start_date: initial?.start_date || '',
        end_date: '',
        type: 'Urlaub', half_day: false, note: '',
      });
    }
    setSuche('');
  }, [open, editRange, initial]);

  const selectedEmp = employees.find(e => e.id === form.employee_id);

  const gefundeneMitarbeiter = useMemo(() => {
    const aktive = employees
      .filter(e => e.active)
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
    const q = suche.trim().toLowerCase();
    if (!q) return aktive;
    return aktive.filter(e => `${e.first_name} ${e.last_name}`.toLowerCase().includes(q));
  }, [employees, suche]);
  const availableTypes = useMemo(() => {
    if (selectedEmp?.assignment_context === 'device') return ABSENCE_TYPES.filter(t => t !== 'Ersatz Geräte');
    return ABSENCE_TYPES;
  }, [selectedEmp?.assignment_context]);

  const submit = async () => {
    if (!form.employee_id || !form.start_date) { toast.error('Mitarbeiter und Startdatum sind Pflicht.'); return; }
    setLoading(true);
    try {
      // Ganztägige Abwesenheiten nehmen den Mitarbeiter aus der Planung.
      // Wie viele Einsätze das betraf, meldet der Server zurück – das muss
      // man sagen, sonst verschwinden Einsätze unbemerkt.
      let r;
      if (isEdit) {
        r = await api.patch('/absences/range', {
          item_ids: editRange.item_ids,
          employee_id: form.employee_id,
          start_date: form.start_date,
          end_date: form.end_date || form.start_date,
          type: form.type, half_day: form.half_day, note: form.note,
        });
        toast.success('Abwesenheit aktualisiert.' + planungsHinweis(r));
      } else if (form.end_date && form.end_date !== form.start_date) {
        r = await api.post('/absences/range', form);
        toast.success('Abwesenheit eingetragen.' + planungsHinweis(r));
      } else {
        r = await api.post('/absences', { employee_id: form.employee_id, date: form.start_date, type: form.type, half_day: form.half_day, note: form.note });
        toast.success('Abwesenheit eingetragen.' + planungsHinweis(r));
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  const del = async () => {
    if (!editRange) return;
    if (!(await confirm({
      title: 'Abwesenheit löschen?',
      description: `Zeitraum ${fmtDate(editRange.start_date)}${editRange.end_date !== editRange.start_date ? ' – ' + fmtDate(editRange.end_date) : ''} wird entfernt.`,
      variant: 'destructive', confirmLabel: 'Löschen',
    }))) return;
    setLoading(true);
    try {
      for (const id of editRange.item_ids) await api.delete(`/absences/${id}`);
      toast.success('Abwesenheit gelöscht.');
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  const isPattern = editRange?.source === 'pattern';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="absence-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plane size={18} />{isEdit ? 'Abwesenheit bearbeiten' : 'Abwesenheit eintragen'}</DialogTitle>
          {isPattern && <DialogDescription className="text-amber-700">Dies ist ein automatischer Schulmuster-Eintrag und kann hier nicht bearbeitet werden.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Mitarbeiter</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={isPattern}>
              <SelectTrigger data-testid="absence-employee"><SelectValue placeholder="Wählen..." /></SelectTrigger>
              <SelectContent>
                {/* Suchfeld innerhalb der Liste. onKeyDown stoppt die
                    Tastatursteuerung des Auswahlfelds – ohne das würde jeder
                    Buchstabe zum Sprung auf einen Namen führen statt im Feld
                    zu landen. */}
                <div className="px-2 py-1.5 sticky top-0 bg-white z-10 border-b border-slate-100">
                  <Input
                    autoFocus
                    value={suche}
                    onChange={e => setSuche(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    placeholder="Name suchen…"
                    className="h-8 text-sm"
                    data-testid="absence-employee-search"
                  />
                </div>
                {gefundeneMitarbeiter.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}
                {gefundeneMitarbeiter.length === 0 && (
                  <div className="px-2 py-3 text-sm text-slate-500">Kein Treffer.</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Von</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="absence-start" disabled={isPattern} /></div>
            <div><Label>Bis (optional)</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="absence-end" disabled={isPattern} /></div>
          </div>
          <div>
            <Label>Art</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={isPattern}>
              <SelectTrigger data-testid="absence-type"><SelectValue /></SelectTrigger>
              <SelectContent>{availableTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.half_day} onChange={e => setForm({ ...form, half_day: e.target.checked })} disabled={isPattern} data-testid="absence-halfday" />
            Halbtags (zählt als 0,5 Tage)
          </label>
          <div>
            <Label>Notiz (optional)</Label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                   placeholder="Grund oder Hinweis" disabled={isPattern} data-testid="absence-note" />
          </div>
        </div>
        <DialogFooter className="flex items-center sm:justify-between gap-2">
          <div>
            {isEdit && !isPattern && (
              <Button variant="ghost" className="text-rose-600" onClick={del} disabled={loading} data-testid="absence-delete"><Trash2 size={14} className="mr-1" />Löschen</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            {!isPattern && <Button onClick={submit} disabled={loading} data-testid="absence-submit">{isEdit ? 'Speichern' : 'Eintragen'}</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SchoolPatternDialog({ open, onOpenChange, employees, onChanged }) {
  const [empId, setEmpId] = useState('');
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [startKw, setStartKw] = useState(15);
  const [endKw, setEndKw] = useState(40);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setEmpId(''); setSelectedDays(new Set()); setStartKw(15); setEndKw(40); setYear(new Date().getFullYear()); }
  }, [open]);

  const toggleDay = (d) => { const next = new Set(selectedDays); next.has(d) ? next.delete(d) : next.add(d); setSelectedDays(next); };

  const submit = async () => {
    if (!empId) { toast.error('Bitte Mitarbeiter wählen.'); return; }
    if (selectedDays.size === 0) { toast.error('Mindestens einen Wochentag wählen.'); return; }
    setLoading(true);
    try {
      for (const d of selectedDays) {
        await api.post('/school-patterns', { employee_id: empId, weekday: d, start_kw: Number(startKw), end_kw: Number(endKw), year: Number(year), active: true });
      }
      toast.success(`${selectedDays.size} Schulmuster erstellt.`);
      onChanged?.(); onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  const azubis = employees.filter(e => e.active && e.type === 'Azubi');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap size={18} />Schulmuster anlegen</DialogTitle>
          <DialogDescription>Erzeugt automatische Abwesenheits-Einträge an den gewählten Wochentagen im Zeitraum.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Azubi</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="pattern-employee"><SelectValue placeholder="Wählen..." /></SelectTrigger>
              <SelectContent>
                {azubis.length === 0 && <div className="px-2 py-1.5 text-sm text-slate-500">Keine Azubis aktiv</div>}
                {azubis.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Schultage</Label>
            <div className="flex gap-1.5 mt-1.5" data-testid="weekday-selector">
              {WEEKDAYS.map(w => (
                <button key={w.value} type="button" onClick={() => toggleDay(w.value)}
                  className={`flex-1 py-2 rounded border text-sm font-bold transition-colors ${selectedDays.has(w.value) ? 'bg-amber-500 text-white border-amber-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`weekday-${w.label}`}>{w.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Von KW</Label><Input type="number" min="1" max="53" value={startKw} onChange={e => setStartKw(e.target.value)} /></div>
            <div><Label className="text-xs">Bis KW</Label><Input type="number" min="1" max="53" value={endKw} onChange={e => setEndKw(e.target.value)} /></div>
            <div><Label className="text-xs">Jahr</Label><Input type="number" value={year} onChange={e => setYear(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading || selectedDays.size === 0} data-testid="pattern-submit">Muster anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditSchoolPatternDialog({ open, onOpenChange, data, employees, onChanged }) {
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [startKw, setStartKw] = useState(15);
  const [endKw, setEndKw] = useState(40);
  const [year, setYear] = useState(new Date().getFullYear());
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && data) { setSelectedDays(new Set(data.weekdays)); setStartKw(data.kwStart); setEndKw(data.kwEnd); setYear(data.year); setActive(data.active); }
  }, [open, data]);

  const emp = data ? employees.find(e => e.id === data.employeeId) : null;
  const toggleDay = (d) => { const next = new Set(selectedDays); next.has(d) ? next.delete(d) : next.add(d); setSelectedDays(next); };

  const submit = async () => {
    if (selectedDays.size === 0) { toast.error('Mindestens einen Wochentag wählen.'); return; }
    setLoading(true);
    try {
      const existingByWeekday = {};
      for (const p of data.existingPats) existingByWeekday[p.weekday] = p;
      for (const d of selectedDays) {
        const existing = existingByWeekday[d];
        if (existing) await api.patch(`/school-patterns/${existing.id}`, { start_kw: Number(startKw), end_kw: Number(endKw), year: Number(year), active });
        else await api.post('/school-patterns', { employee_id: data.employeeId, weekday: d, start_kw: Number(startKw), end_kw: Number(endKw), year: Number(year), active });
      }
      for (const p of data.existingPats) if (!selectedDays.has(p.weekday)) await api.delete(`/school-patterns/${p.id}`);
      toast.success('Schulmuster aktualisiert.');
      onChanged?.(); onOpenChange(false);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap size={18} />Schulmuster bearbeiten</DialogTitle>
          <DialogDescription>Muster für <strong>{emp ? `${emp.first_name} ${emp.last_name}` : '-'}</strong>. Änderungen regenerieren alle automatischen Einträge.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Schultage</Label>
            <div className="flex gap-1.5 mt-1.5" data-testid="edit-weekday-selector">
              {WEEKDAYS.map(w => (
                <button key={w.value} type="button" onClick={() => toggleDay(w.value)}
                  className={`flex-1 py-2 rounded border text-sm font-bold transition-colors ${selectedDays.has(w.value) ? 'bg-amber-500 text-white border-amber-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`edit-weekday-${w.label}`}>{w.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Von KW</Label><Input type="number" min="1" max="53" value={startKw} onChange={e => setStartKw(e.target.value)} /></div>
            <div><Label className="text-xs">Bis KW</Label><Input type="number" min="1" max="53" value={endKw} onChange={e => setEndKw(e.target.value)} /></div>
            <div><Label className="text-xs">Jahr</Label><Input type="number" value={year} onChange={e => setYear(e.target.value)} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Aktiv
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={loading || selectedDays.size === 0} data-testid="edit-pattern-submit">Änderungen speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Mitarbeiter-Detail: komplette Abwesenheits-Analysen */
export function EmployeeAbsenceDetailDialog({ open, onOpenChange, employeeId, year, costCenterName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employeeId) return;
    setLoading(true);
    api.get(`/absence-analytics/${employeeId}`, { params: { year } })
      .then(r => setData(r.data))
      .catch(err => toast.error(extractError(err)))
      .finally(() => setLoading(false));
  }, [open, employeeId, year]);

  const maxWd = data ? Math.max(1, ...data.sickness.weekday_counts.slice(0, 5)) : 1;

  // Auffälliges Muster erkennen (z.B. häufige Montags-Krankmeldungen)
  const WD_FULL = ['montags', 'dienstags', 'mittwochs', 'donnerstags', 'freitags'];
  const pattern = useMemo(() => {
    if (!data) return null;
    const wc = data.sickness.weekday_counts.slice(0, 5);
    const total = wc.reduce((s, c) => s + c, 0);
    if (total < 3) return null;
    let domIdx = 0;
    wc.forEach((c, i) => { if (c > wc[domIdx]) domIdx = i; });
    const domCount = wc[domIdx];
    const share = domCount / total;
    if (domCount >= 2 && share >= 0.4) {
      return { idx: domIdx, count: domCount, total, share, label: WD_FULL[domIdx], bridge: domIdx === 0 || domIdx === 4 };
    }
    return null;
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="employee-absence-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Activity size={18} />{data?.employee?.name || 'Analyse'}</DialogTitle>
          <DialogDescription>{costCenterName ? `Kostenstelle: ${costCenterName} · ` : ''}Zyklus {year}{data?.policy_name ? ` · ${data.policy_name}` : ''}</DialogDescription>
        </DialogHeader>
        {loading && <div className="text-sm text-slate-500 py-8 text-center">Lädt Analyse…</div>}
        {data && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat label="Urlaub genommen" value={`${fmtDays(data.vacation.taken)} T`} />
              <MiniStat label="Urlaub gebucht" value={`${fmtDays(data.vacation.booked)} T`} />
              <MiniStat label="Urlaubssaldo" value={`${fmtDays(data.vacation.saldo)} T`} accent />
              <MiniStat label="Anspruch" value={`${fmtDays(data.vacation.total_entitlement)} T`} />
              <MiniStat label="Krankheitstage" value={fmtDays(data.sickness.total_days)} />
              <MiniStat label="Krankheits­fälle" value={data.sickness.cases} />
              <MiniStat label="Unbezahlt" value={`${fmtDays(data.type_counts['Unbezahlter Urlaub'] || 0)} T`} />
              <MiniStat label="Sonstiges" value={`${fmtDays(data.type_counts['Sonstiges'] || 0)} T`} />
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-2"><CalendarDays size={14} />Krankheitstage nach Wochentag</div>
              <div className="flex items-end gap-2 h-28">
                {data.sickness.weekday_counts.slice(0, 5).map((c, i) => {
                  const highlight = pattern && pattern.idx === i;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                      <div className={`text-xs font-mono ${highlight ? 'font-bold text-amber-700' : 'text-slate-600'}`}>{c}</div>
                      <div className={`w-full rounded-t ${highlight ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ height: `${(c / maxWd) * 80}px`, minHeight: c > 0 ? 6 : 2 }} />
                      <div className={`text-[11px] ${highlight ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>{['Mo', 'Di', 'Mi', 'Do', 'Fr'][i]}</div>
                    </div>
                  );
                })}
              </div>
              {data.sickness.total_days === 0 && <div className="text-xs text-slate-400 mt-2">Keine Krankheitstage im Zyklus.</div>}
              {pattern && (
                <div className="mt-3 flex items-start gap-2 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-2.5 py-2" data-testid="sickness-pattern">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Auffälliges Muster:</strong> {pattern.count} von {pattern.total} Krankmeldungen fallen auf {pattern.label} ({Math.round(pattern.share * 100)} %).
                    {pattern.bridge && ' Häufungen an Brückentagen (Montag/Freitag) können auf verlängerte Wochenenden hindeuten.'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {Object.entries(data.type_counts).map(([t, c]) => (
                <Badge key={t} variant="outline" className={TYPE_BADGE[t] || ''}>{t}: {fmtDays(c)} Tage</Badge>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`rounded-md border p-2.5 ${accent ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className="text-[11px] text-slate-500 font-semibold leading-tight">{label}</div>
      <div className={`text-lg font-bold font-mono ${accent ? 'text-amber-700' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}
